import { readLeaderSessionAgreement, removeLegacyCoachFeedbackFromGeneratedPlan } from "../../supabase/functions/_shared/leaderSessionAgreement";
import type {
  FrameworkPackDefinition,
  FrameworkPackId,
} from "@/lib/behavior/frameworkPacks";

type LegacyAnalysisRecord = Record<string, unknown>;
type CoachReviewMode = "grounded" | "general";

export interface CoachFrameworkSummary {
  name: string;
  summary: string | null;
}

export interface CoachFrameworkOperationalizationSignalSnapshot {
  id: string;
  label: string;
  evidenceHints: string[];
  positiveIndicators: string[];
  prohibitedInferences: string[];
}

export interface CoachFrameworkOperationalizationReflectionSnapshot {
  id: string;
  label: string;
  useWhen: string[];
}

export interface CoachFrameworkOperationalizationSnapshot {
  frameworkPackId: FrameworkPackId;
  frameworkPackLabel: string;
  noticingSignals: CoachFrameworkOperationalizationSignalSnapshot[];
  reflectionPatterns: CoachFrameworkOperationalizationReflectionSnapshot[];
  boundaries: {
    allowed: string[];
    prohibited: string[];
    coachOnly: string[];
  };
}

export interface CoachStyleContextSnapshot {
  present: boolean;
  lensName: string | null;
  philosophy: string | null;
  frameworks: CoachFrameworkSummary[];
  frameworkOperationalization: CoachFrameworkOperationalizationSnapshot | null;
  knowledgeCoverage: "none" | "partial" | "full";
  preferredLanguage: string[];
  avoidLanguage: string[];
}

export interface CoachDevelopmentContextSnapshot {
  nodeCounts: Record<string, number>;
  recentThemes: string[];
}

export interface CoachVoiceContextSnapshot {
  present: boolean;
  voiceSummary: string | null;
  questionStems: string[];
  phraseBank: string[];
  avoidList: string[];
  guardrails: string[];
}

export interface CoachSessionAnalysisInputBucket {
  version: "coach_session_input_bucket_v1";
  reviewMode: CoachReviewMode;
  artifact: {
    transcriptUploadId: string;
    leaderId: string;
    meetingId: string | null;
    createdAt: string | null;
  };
  transcript: {
    summaryText: string | null;
    analysisContext: string | null;
    reportVersion: string | null;
  };
  coachStyleContext: CoachStyleContextSnapshot;
  coachDevelopmentContext: CoachDevelopmentContextSnapshot;
  coachVoiceContext: CoachVoiceContextSnapshot;
}

export interface CoachReviewV1 {
  version: "coach_review_v1";
  analysisLens: "declared_coach_lens" | "executive_coaching_default";
  coachingStrengths: string[];
  coachingGrowthEdges: string[];
  nextCoachingExperiment: string | null;
  reflectiveQuestions: string[];
  frameworkAlignmentNotes: string[];
  deliveryStyleNotes: string[];
  coachCraftObservations?: CoachCraftObservationsV1;
}

export type CoachCraftObservationBasis =
  | "observable_craft"
  | "professional_standard"
  | "industry_coaching_framework"
  | "leadership_framework"
  | "coach_configured_framework"
  | "coach_memory_pattern";

export type CoachCraftObservationConfidence = "low" | "medium" | "high";

export interface CoachCraftObservationV1 {
  label: string;
  basis: CoachCraftObservationBasis[];
  evidence: string[];
  confidence: CoachCraftObservationConfidence;
  limitation?: string | null;
}

export interface CoachCraftObservationsV1 {
  version: "coach_craft_observations_v1";
  sessionCraft: {
    contracting: CoachCraftObservationV1[];
    trustAndSafety: CoachCraftObservationV1[];
    presenceAndPacing: CoachCraftObservationV1[];
    activeListening: CoachCraftObservationV1[];
    evokingAwareness: CoachCraftObservationV1[];
    clientAutonomy: CoachCraftObservationV1[];
    learningAndAccountability: CoachCraftObservationV1[];
  };
  strengths: CoachCraftObservationV1[];
  growthEdges: CoachCraftObservationV1[];
  nextExperiment: {
    text: string;
    why: string;
  } | null;
  memoryPattern: {
    status: "session_observation" | "possible_pattern" | "recurring_pattern";
    sessionCount: number;
    note: string;
  } | null;
}

export interface LeaderPlanDraftV1 {
  version: "leader_plan_draft_v1";
  approvalState: "pending" | "approved" | "rejected";
  whatDiscussed: string[];
  nextFocus: string[];
  nextSession: string[];
  actionItems: string[];
  reflectionPrompts: string[];
}

export interface CoachSessionArtifactsV1 {
  coachReview: CoachReviewV1;
  leaderPlanDraft: LeaderPlanDraftV1;
}

export interface CoachSessionEditorState {
  coachGrowth: CoachGrowthEditorState;
  leaderAgreement: LeaderAgreementEditorState;
}

export interface CoachLensSource {
  lens_name?: string | null;
  updatedAt?: string | null;
  frameworks?: Array<{ name?: string | null; summary?: string | null }> | null;
  preferred_language?: string[] | null;
  avoid_language?: string[] | null;
  coach_lens_json?: {
    philosophy?: string | null;
    frameworks_raw?: string | null;
    frameworks_normalized?: string | null;
    framework_pack_id?: FrameworkPackId | null;
    framework_pack?: FrameworkPackDefinition | null;
    identity_links?: Array<{
      id?: string | null;
      title?: string | null;
      sourceUrl?: string | null;
      notes?: Array<string | null | undefined>;
    }> | null;
    identity_report?: {
      status?: "empty" | "parsed" | "partial" | "failed" | null;
      parsedSectionCount?: number | null;
      referenceCount?: number | null;
      parsedReferenceCount?: number | null;
      issues?: Array<{
        level?: "warning" | "error" | null;
        field?: string | null;
        message?: string | null;
      }> | null;
    } | null;
  } | null;
}

export interface CoachTwinSource {
  voice_summary?: string | null;
  question_stems?: string[] | null;
  phrase_bank?: string[] | null;
  avoid_list?: string[] | null;
  guardrails?: string[] | null;
  identity_template_preview?: {
    lensLabel?: string | null;
    philosophySummary?: string | null;
    voiceSummary?: string | null;
    frameworkPackId?: FrameworkPackId | null;
    frameworkPackLabel?: string | null;
    voiceCustomizationStatus?: "customized" | "framework_default" | "default" | null;
    referenceCount?: number | null;
    parsedReferenceCount?: number | null;
  } | null;
}

export interface CoachMemorySource {
  nodeCounts?: Record<string, number>;
  recentNodes?: Array<{ node_type?: string | null; title?: string | null; content?: string | null }>;
}

export interface CoachGrowthEditorState {
  worked: string[];
  growthEdges: string[];
  nextExperiment?: string | null;
  reflectiveQuestions: string[];
}

export interface LeaderAgreementEditorState {
  discussed: string[];
  nextFocus: string[];
  nextSession: string[];
}

function normalizeLine(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is string => typeof item === "string")
    .map(normalizeLine)
    .filter(Boolean);
}

function dedupe(values: Array<string | null | undefined>, limit = 5): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    if (!value) continue;
    const normalized = normalizeLine(value);
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(normalized);
    if (result.length >= limit) break;
  }
  return result;
}

function buildFrameworkOperationalizationSnapshot(
  frameworkPack: FrameworkPackDefinition | null | undefined,
): CoachFrameworkOperationalizationSnapshot | null {
  if (!frameworkPack) return null;

  return {
    frameworkPackId: frameworkPack.id,
    frameworkPackLabel: frameworkPack.label,
    noticingSignals: frameworkPack.operationalization.noticingSignals.map((signal) => ({
      id: signal.id,
      label: signal.label,
      evidenceHints: signal.evidenceHints,
      positiveIndicators: signal.positiveIndicators,
      prohibitedInferences: signal.prohibitedInferences,
    })),
    reflectionPatterns: frameworkPack.operationalization.reflectionPatterns.map((pattern) => ({
      id: pattern.id,
      label: pattern.label,
      useWhen: pattern.useWhen,
    })),
    boundaries: {
      allowed: frameworkPack.operationalization.boundaries.allowed,
      prohibited: frameworkPack.operationalization.boundaries.prohibited,
      coachOnly: frameworkPack.operationalization.boundaries.coachOnly,
    },
  };
}

function sentenceCandidates(raw: string): string[] {
  return raw
    .split(/(?<=[.!?])\s+/)
    .map(normalizeLine)
    .filter((line) => line.length >= 10);
}

function flattenObjectStrings(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap((item) => {
      if (typeof item === "string") return [item];
      if (item && typeof item === "object") {
        return Object.values(item as Record<string, unknown>).filter((entry): entry is string => typeof entry === "string");
      }
      return [];
    });
  }
  if (typeof value === "string") return sentenceCandidates(value);
  return [];
}

function extractReportV1(analysisResult: unknown): LegacyAnalysisRecord | null {
  if (!analysisResult || typeof analysisResult !== "object") return null;
  const report = (analysisResult as { report_v1?: unknown }).report_v1;
  if (!report || typeof report !== "object") return null;
  const version = (report as { version?: unknown }).version;
  return version === "transcript_report_v1" ? report as LegacyAnalysisRecord : null;
}

export function buildCoachSessionAnalysisInputBucket(params: {
  transcriptUploadId: string;
  leaderId: string;
  meetingId: string | null;
  createdAt?: string | null;
  analysisResult: unknown;
  summaryText?: string | null;
  coachLens?: CoachLensSource | null;
  coachTwin?: CoachTwinSource | null;
  coachMemory?: CoachMemorySource | null;
  coachReviewMode?: CoachReviewMode | null;
}): CoachSessionAnalysisInputBucket {
  const report = extractReportV1(params.analysisResult);
  const frameworks = (params.coachLens?.frameworks || [])
    .map((item) => ({
      name: normalizeLine(item?.name || ""),
      summary: item?.summary ? normalizeLine(item.summary) : null,
    }))
    .filter((item) => item.name.length > 0);
  const frameworkPack = params.coachLens?.coach_lens_json?.framework_pack || null;
  const frameworkSummaryCount = frameworks.filter((item) => item.summary).length;
  const philosophy = params.coachLens?.coach_lens_json?.philosophy
    ? normalizeLine(params.coachLens.coach_lens_json.philosophy)
    : null;

  let knowledgeCoverage: CoachStyleContextSnapshot["knowledgeCoverage"] = "none";
  if (frameworks.length > 0) {
    knowledgeCoverage = frameworkSummaryCount === frameworks.length ? "full" : "partial";
  }

  const recentThemes = (params.coachMemory?.recentNodes || [])
    .flatMap((node) => [node.title, node.content])
    .filter((value): value is string => typeof value === "string")
    .map((value) => normalizeLine(value).slice(0, 140))
    .filter((value) => value.length > 0)
    .slice(0, 5);

  const inferredReviewMode =
    params.coachReviewMode
    || ((params.analysisResult && typeof params.analysisResult === "object"
      ? (((params.analysisResult as { _meta?: { coach_review_mode?: unknown } })._meta?.coach_review_mode as CoachReviewMode | undefined) ?? null)
      : null))
    || ((philosophy || frameworks.length > 0 || params.coachLens?.lens_name) ? "grounded" : "general");

  return {
    version: "coach_session_input_bucket_v1",
    reviewMode: inferredReviewMode,
    artifact: {
      transcriptUploadId: params.transcriptUploadId,
      leaderId: params.leaderId,
      meetingId: params.meetingId,
      createdAt: params.createdAt ?? null,
    },
    transcript: {
      summaryText: params.summaryText ? normalizeLine(params.summaryText) : null,
      analysisContext: typeof report?.processing_state === "object"
        ? ((report.processing_state as { analysis_context?: string }).analysis_context ?? null)
        : null,
      reportVersion: typeof report?.version === "string" ? report.version : null,
    },
    coachStyleContext: {
      present: Boolean(philosophy || frameworks.length > 0 || params.coachLens?.lens_name),
      lensName: params.coachLens?.lens_name ? normalizeLine(params.coachLens.lens_name) : null,
      philosophy,
      frameworks,
      frameworkOperationalization: buildFrameworkOperationalizationSnapshot(frameworkPack),
      knowledgeCoverage,
      preferredLanguage: toStringArray(params.coachLens?.preferred_language),
      avoidLanguage: toStringArray(params.coachLens?.avoid_language),
    },
    coachDevelopmentContext: {
      nodeCounts: params.coachMemory?.nodeCounts || {},
      recentThemes,
    },
    coachVoiceContext: {
      present: Boolean(
        params.coachTwin?.voice_summary
        || params.coachTwin?.question_stems?.length
        || params.coachTwin?.phrase_bank?.length
      ),
      voiceSummary: params.coachTwin?.voice_summary ? normalizeLine(params.coachTwin.voice_summary) : null,
      questionStems: toStringArray(params.coachTwin?.question_stems),
      phraseBank: toStringArray(params.coachTwin?.phrase_bank),
      avoidList: toStringArray(params.coachTwin?.avoid_list),
      guardrails: toStringArray(params.coachTwin?.guardrails),
    },
  };
}

function buildFrameworkAlignmentNotes(bucket: CoachSessionAnalysisInputBucket): string[] {
  if (!bucket.coachStyleContext.present) return [];
  const frameworkNames = bucket.coachStyleContext.frameworks.map((item) => item.name);
  const frameworkSummaryNotes = bucket.coachStyleContext.frameworks
    .filter((item) => item.summary)
    .slice(0, 2)
    .map((item) => `Framework guidance: ${item.name} - ${item.summary}`);
  return dedupe([
    bucket.coachStyleContext.philosophy
      ? `Declared philosophy in play: ${bucket.coachStyleContext.philosophy}`
      : null,
    frameworkNames.length > 0
      ? `Coach-declared frameworks to apply during review: ${frameworkNames.slice(0, 4).join(", ")}`
      : null,
    ...frameworkSummaryNotes,
    bucket.coachStyleContext.knowledgeCoverage === "partial"
      ? "Framework knowledge coverage is partial. Weight transcript evidence more heavily than framework inference."
      : null,
  ], 5);
}

function buildDeliveryStyleNotes(bucket: CoachSessionAnalysisInputBucket): string[] {
  if (!bucket.coachVoiceContext.present) return [];
  return dedupe([
    bucket.coachVoiceContext.voiceSummary
      ? `Mirror this coach voice in delivery: ${bucket.coachVoiceContext.voiceSummary}`
      : null,
    bucket.coachVoiceContext.questionStems.length > 0
      ? `Preferred question stems: ${bucket.coachVoiceContext.questionStems.slice(0, 3).join("; ")}`
      : null,
    bucket.coachVoiceContext.guardrails.length > 0
      ? `Voice guardrails: ${bucket.coachVoiceContext.guardrails.slice(0, 3).join("; ")}`
      : null,
  ], 3);
}

function extractLegacyCoachSessionSignals(analysisResult: unknown, summaryText?: string | null) {
  const report = extractReportV1(analysisResult);
  const legacy = (analysisResult && typeof analysisResult === "object")
    ? analysisResult as LegacyAnalysisRecord
    : {};

  const strengths = report
    ? flattenObjectStrings((report.behavior_analysis as LegacyAnalysisRecord | undefined)?.strengths_observed)
    : flattenObjectStrings(legacy.strengths_observed);
  const growth = report
    ? flattenObjectStrings((report.behavior_analysis as LegacyAnalysisRecord | undefined)?.growth_edges)
    : flattenObjectStrings(legacy.growth_edges);
  const nextExperiment = report
    ? (((report.recommended_next_step as LegacyAnalysisRecord | undefined)?.next_experiment as string | undefined) ?? null)
    : (typeof legacy.next_experiment === "string" ? legacy.next_experiment : null);
  const coachPrompt = report
    ? ((report.recommended_next_step as LegacyAnalysisRecord | undefined)?.coach_discussion_prompt as string | undefined)
    : (typeof legacy.coach_discussion_prompt === "string" ? legacy.coach_discussion_prompt : undefined);
  const summary = summaryText
    || (report
      ? ((report.conversation_summary as LegacyAnalysisRecord | undefined)?.summary_text as string | undefined)
      : (typeof legacy.summary_text === "string" ? legacy.summary_text : undefined));
  const keyInsight = report
    ? ((report.coaching_insights as LegacyAnalysisRecord | undefined)?.key_insight as string | undefined)
    : (typeof legacy.key_insight === "string" ? legacy.key_insight : undefined);
  return {
    strengths,
    growth,
    nextExperiment,
    coachPrompt,
    summary,
    keyInsight,
  };
}

function legacyAnalysisToGrowth(analysisResult: unknown, summaryText?: string | null): {
  coachGrowth: CoachGrowthEditorState;
  leaderAgreement: LeaderAgreementEditorState;
} {
  const {
    strengths,
    growth,
    nextExperiment,
    coachPrompt,
    keyInsight,
  } = extractLegacyCoachSessionSignals(analysisResult, summaryText);

  return {
    coachGrowth: {
      worked: dedupe(strengths, 3),
      growthEdges: dedupe([
        ...growth,
        keyInsight,
      ], 3),
      nextExperiment: nextExperiment ? normalizeLine(nextExperiment) : null,
      reflectiveQuestions: dedupe([
        coachPrompt,
        "Where was I most effective in helping the leader think for themselves?",
        "What coaching move could I simplify or sharpen next session?",
        "What evidence should I listen for to validate progress?",
      ], 3),
    },
    leaderAgreement: readLeaderSessionAgreement(analysisResult) || {
      discussed: [], nextFocus: [], nextSession: [],
    },
  };
}

export function buildCoachSessionArtifactsFromAnalysis(params: {
  bucket: CoachSessionAnalysisInputBucket;
  analysisResult: unknown;
  summaryText?: string | null;
}): CoachSessionArtifactsV1 {
  const derived = legacyAnalysisToGrowth(params.analysisResult, params.summaryText);
  return buildCoachSessionArtifactsFromEditor({
    bucket: params.bucket,
    coachGrowth: derived.coachGrowth,
    leaderAgreement: derived.leaderAgreement,
  });
}

export function buildCoachSessionArtifactsFromEditor(params: {
  bucket: CoachSessionAnalysisInputBucket;
  coachGrowth: CoachGrowthEditorState;
  leaderAgreement: LeaderAgreementEditorState;
  approvalState?: LeaderPlanDraftV1["approvalState"];
}): CoachSessionArtifactsV1 {
  const review: CoachReviewV1 = {
    version: "coach_review_v1",
    analysisLens: params.bucket.reviewMode === "grounded" && params.bucket.coachStyleContext.present
      ? "declared_coach_lens"
      : "executive_coaching_default",
    coachingStrengths: dedupe(params.coachGrowth.worked, 4),
    coachingGrowthEdges: dedupe(params.coachGrowth.growthEdges, 4),
    nextCoachingExperiment: params.coachGrowth.nextExperiment
      ? normalizeLine(params.coachGrowth.nextExperiment)
      : null,
    reflectiveQuestions: dedupe(params.coachGrowth.reflectiveQuestions, 4),
    frameworkAlignmentNotes: params.bucket.reviewMode === "grounded"
      ? buildFrameworkAlignmentNotes(params.bucket)
      : [],
    deliveryStyleNotes: params.bucket.reviewMode === "grounded"
      ? buildDeliveryStyleNotes(params.bucket)
      : [],
  };
  const leaderPlanDraft: LeaderPlanDraftV1 = {
    version: "leader_plan_draft_v1",
    approvalState: params.approvalState ?? "pending",
    whatDiscussed: dedupe(params.leaderAgreement.discussed, 4),
    nextFocus: dedupe(params.leaderAgreement.nextFocus, 4),
    nextSession: dedupe(params.leaderAgreement.nextSession, 4),
    actionItems: dedupe([
      ...params.leaderAgreement.nextFocus,
    ], 4),
    reflectionPrompts: dedupe(params.leaderAgreement.nextSession, 4),
  };

  return { coachReview: review, leaderPlanDraft };
}

export function buildCoachSessionArtifactsFromNotes(notes: string): CoachSessionArtifactsV1 {
  const candidates = sentenceCandidates(notes);
  const worked = candidates.slice(0, 3);
  const nextFocus = candidates
    .filter((line) => /\b(we are|we will|focus|continue|practice|experiment|next)\b/i.test(line))
    .slice(0, 3);
  const nextSession = candidates
    .filter((line) => /\b(next|listen|watch|notice|expect|bring|review)\b/i.test(line))
    .slice(0, 3);

  return {
    coachReview: {
      version: "coach_review_v1",
      analysisLens: "executive_coaching_default",
      coachingStrengths: worked,
      coachingGrowthEdges: nextFocus,
      nextCoachingExperiment: nextFocus[0] || null,
      reflectiveQuestions: dedupe([
        "What coaching move do I want to repeat next session?",
        "What evidence should I listen for in the leader's follow-through?",
      ], 2),
      frameworkAlignmentNotes: [],
      deliveryStyleNotes: [],
    },
    leaderPlanDraft: {
      version: "leader_plan_draft_v1",
      approvalState: "pending",
      whatDiscussed: worked,
      nextFocus,
      nextSession,
      actionItems: dedupe([...nextFocus, ...nextSession], 4),
      reflectionPrompts: dedupe([
        nextFocus[0] || null,
        nextSession[0] || null,
      ], 3),
    },
  };
}

export function extractCoachReviewV1(value: unknown): CoachReviewV1 | null {
  if (!value || typeof value !== "object") return null;
  return (value as { version?: unknown }).version === "coach_review_v1"
    ? value as CoachReviewV1
    : null;
}

export function extractLeaderPlanDraftV1(value: unknown): LeaderPlanDraftV1 | null {
  if (!value || typeof value !== "object") return null;
  return (value as { version?: unknown }).version === "leader_plan_draft_v1"
    ? value as LeaderPlanDraftV1
    : null;
}

export function extractCoachSessionSeedV1(value: unknown): CoachSessionArtifactsV1 | null {
  if (!value || typeof value !== "object") return null;
  const record = value as { coachReview?: unknown; leaderPlanDraft?: unknown };
  const coachReview = extractCoachReviewV1(record.coachReview);
  const leaderPlanDraft = extractLeaderPlanDraftV1(record.leaderPlanDraft);
  if (!coachReview || !leaderPlanDraft) return null;
  return {
    coachReview,
    leaderPlanDraft,
  };
}

export function deriveCoachSessionPublishedState(params: {
  publishedToLeader?: boolean | null;
  leaderPlanDraft?: LeaderPlanDraftV1 | null;
}): boolean {
  if (params.publishedToLeader) return true;
  return params.leaderPlanDraft?.approvalState === "approved";
}

export function resolveCoachSessionArtifacts(params: {
  bucket: CoachSessionAnalysisInputBucket;
  analysisResult: unknown;
  summaryText?: string | null;
  savedCoachReview?: unknown;
  savedLeaderPlan?: unknown;
  publishedToLeader?: boolean | null;
}) {
  const persistedCoachReview = extractCoachReviewV1(params.savedCoachReview);
  const persistedLeaderPlan = extractLeaderPlanDraftV1(params.savedLeaderPlan);
  const seededArtifacts = extractCoachSessionSeedV1(
    params.analysisResult && typeof params.analysisResult === "object"
      ? (params.analysisResult as { coach_session_seed_v1?: unknown }).coach_session_seed_v1
      : null,
  );
  const analysisArtifacts = buildCoachSessionArtifactsFromAnalysis({
    bucket: params.bucket,
    analysisResult: params.analysisResult,
    summaryText: params.summaryText,
  });
  const derivedArtifacts = seededArtifacts ? {
    coachReview: seededArtifacts.coachReview,
    leaderPlanDraft: readLeaderSessionAgreement(params.analysisResult)
      ? analysisArtifacts.leaderPlanDraft
      : removeLegacyCoachFeedbackFromGeneratedPlan(seededArtifacts.leaderPlanDraft, params.analysisResult, params.summaryText),
  } : analysisArtifacts;
  const coachReview = persistedCoachReview || derivedArtifacts.coachReview;
  const leaderPlanDraft = persistedLeaderPlan || derivedArtifacts.leaderPlanDraft;
  const publishedToLeader = deriveCoachSessionPublishedState({
    publishedToLeader: params.publishedToLeader,
    leaderPlanDraft,
  });

  return {
    coachReview,
    leaderPlanDraft,
    persistedCoachReview,
    persistedLeaderPlan,
    seededArtifacts,
    derivedArtifacts,
    publishedToLeader,
    source: persistedCoachReview || persistedLeaderPlan ? "saved" as const : "derived" as const,
  };
}

export function projectLeaderPlanDraftToLegacyRecord(
  plan: LeaderPlanDraftV1,
): Pick<LeaderAgreementEditorState, "discussed" | "nextFocus" | "nextSession"> {
  return {
    discussed: dedupe(plan.whatDiscussed, 4),
    nextFocus: dedupe(plan.nextFocus.length > 0 ? plan.nextFocus : plan.actionItems, 4),
    nextSession: dedupe(plan.nextSession.length > 0 ? plan.nextSession : plan.reflectionPrompts, 4),
  };
}

export function projectCoachReviewToEditorState(review: CoachReviewV1): CoachGrowthEditorState {
  return {
    worked: dedupe(review.coachingStrengths, 4),
    growthEdges: dedupe(review.coachingGrowthEdges, 4),
    nextExperiment: review.nextCoachingExperiment ? normalizeLine(review.nextCoachingExperiment) : null,
    reflectiveQuestions: dedupe(review.reflectiveQuestions, 4),
  };
}

export function mergeLeaderPlanDraftWithEditorState(params: {
  existingDraft: LeaderPlanDraftV1 | null;
  leaderAgreement: LeaderAgreementEditorState;
  approvalState?: LeaderPlanDraftV1["approvalState"];
}): LeaderPlanDraftV1 {
  const fallbackActionItems = dedupe([
    ...params.leaderAgreement.nextFocus,
  ], 4);
  const fallbackReflectionPrompts = dedupe(params.leaderAgreement.nextSession, 4);

  return {
    version: "leader_plan_draft_v1",
    approvalState: params.approvalState ?? params.existingDraft?.approvalState ?? "pending",
    whatDiscussed: dedupe(params.leaderAgreement.discussed, 4),
    nextFocus: dedupe(params.leaderAgreement.nextFocus, 4),
    nextSession: dedupe(params.leaderAgreement.nextSession, 4),
    actionItems: dedupe(
      params.existingDraft?.actionItems?.length
        ? [...params.leaderAgreement.nextFocus, ...params.existingDraft.actionItems]
        : fallbackActionItems,
      4,
    ),
    reflectionPrompts: dedupe(
      params.existingDraft?.reflectionPrompts?.length
        ? [...params.leaderAgreement.nextSession, ...params.existingDraft.reflectionPrompts]
        : fallbackReflectionPrompts,
      4,
    ),
  };
}

export function projectCoachSessionArtifactsToEditorState(
  artifacts: CoachSessionArtifactsV1,
): CoachSessionEditorState {
  return {
    coachGrowth: projectCoachReviewToEditorState(artifacts.coachReview),
    leaderAgreement: projectLeaderPlanDraftToLegacyRecord(artifacts.leaderPlanDraft),
  };
}
